import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Copy, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface DonationCryptoPaymentData {
  donationId?: string;
  txRef?: string;
  paymentLink?: string;
  transactionId?: string;
  reference?: string;
  address?: string;
  walletAddress?: string;
  network?: string;
  chain?: string;
  crypto?: string;
  baseFiat?: string;
  amountInCrypto?: number;
  platformFeeInCrypto?: number;
  amountInFiat?: number;
  validityPeriod?: number;
  expiresAt?: string;
  amount?: number;
  currency?: string;
  qrCode?: string;
  qrCodeUrl?: string;
  message?: string;
}

interface DonationCryptoPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaid: () => void | Promise<void>;
  isVerifying?: boolean;
  data: DonationCryptoPaymentData | null;
}

export default function DonationCryptoPaymentModal({
  isOpen,
  onClose,
  onPaid,
  isVerifying = false,
  data,
}: DonationCryptoPaymentModalProps) {
  const { t } = useTranslation();
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [generatedQrCode, setGeneratedQrCode] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const walletAddress = data?.address || data?.walletAddress || null;

  useEffect(() => {
    if (!isOpen || !data) return;

    const computeRemaining = () => {
      if (!data.expiresAt) return 0;
      const diff = new Date(data.expiresAt).getTime() - Date.now();
      return Math.max(0, Math.floor(diff / 1000));
    };

    const initialRemaining = computeRemaining();
    setRemainingSeconds(initialRemaining);

    if (typeof data.validityPeriod === "number" && data.validityPeriod > 0) {
      setTotalSeconds(data.validityPeriod);
    } else {
      setTotalSeconds(initialRemaining);
    }

    const interval = window.setInterval(() => {
      setRemainingSeconds(computeRemaining());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isOpen, data]);

  useEffect(() => {
    if (!isOpen || !walletAddress || data?.qrCode || data?.qrCodeUrl) {
      setGeneratedQrCode(null);
      return;
    }

    let cancelled = false;
    QRCode.toDataURL(walletAddress, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: "M",
    })
      .then((dataUrl) => {
        if (!cancelled) {
          setGeneratedQrCode(dataUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGeneratedQrCode(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data?.qrCode, data?.qrCodeUrl, isOpen, walletAddress]);

  useEffect(() => {
    if (copyState === "idle") return;

    const timeout = window.setTimeout(() => setCopyState("idle"), 1800);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  if (!isOpen || !data) return null;

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const fiatCurrency = data.currency || data.baseFiat || "";
  const fiatAmount = typeof data.amountInFiat === "number" ? data.amountInFiat : data.amount;
  const cryptoAmount = data.amountInCrypto ?? undefined;
  const progressRatio = totalSeconds > 0 ? Math.min(1, remainingSeconds / totalSeconds) : 0;
  const progressPercentage = Math.round(progressRatio * 100);
  const isExpired = remainingSeconds <= 0;
  const qrCodeSource = data.qrCode || data.qrCodeUrl || generatedQrCode;

  const handleCopyAddress = async () => {
    if (!walletAddress) return;

    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="max-h-[90vh] w-[92vw] max-w-[620px] overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-[#2a2b2e]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#2a2b2e] sm:p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
            {t("enduser.plan.crypto.title", { defaultValue: "Crypto Payment Details" })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={t("enduser.plan.actions.close", { defaultValue: "Close modal" })}
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-cyan-50 p-4 dark:border-gray-700 dark:from-blue-950/30 dark:via-[#2a2b2e] dark:to-cyan-950/20 sm:p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-24 w-4 items-end overflow-hidden rounded-full bg-blue-100 dark:bg-blue-950/60">
              <div
                className={`w-full rounded-full transition-[height] duration-1000 ${
                  isExpired ? "bg-red-500" : "bg-gradient-to-t from-cyan-500 to-blue-600"
                }`}
                style={{ height: `${isExpired ? 0 : Math.max(progressPercentage, 6)}%` }}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-blue-700/80 dark:text-blue-200/70">
                    {t("enduser.plan.crypto.timeRemaining", { defaultValue: "Time Remaining" })}
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                    {isExpired
                      ? t("enduser.plan.crypto.expired", { defaultValue: "Expired" })
                      : formatCountdown(remainingSeconds)}
                  </p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {isExpired
                      ? t("enduser.plan.crypto.instructionsExpired", {
                          defaultValue: "This payment window has closed. Restart checkout to get a new address.",
                        })
                      : t("enduser.plan.crypto.instructionsCountdown", {
                          defaultValue: "Complete the transfer before the countdown finishes.",
                        })}
                  </p>
                </div>

                <div className="sm:text-right">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
                    {t("enduser.plan.crypto.expiresAt", { defaultValue: "Expires At" })}
                  </p>
                  <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                    {data.expiresAt ? new Date(data.expiresAt).toLocaleString() : "-"}
                  </p>
                  <p className="mt-2 text-xs font-medium text-blue-700 dark:text-blue-200">
                    {isExpired
                      ? t("enduser.plan.crypto.progressExpired", { defaultValue: "0% time left" })
                      : t("enduser.plan.crypto.progressLabel", {
                          defaultValue: `${progressPercentage}% window remaining`,
                        })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-4 sm:p-6">
          <div className="grid items-stretch gap-4 sm:grid-cols-[1.2fr_0.8fr]">
            <div className="flex flex-col justify-between rounded-2xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20 sm:p-5">
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-blue-700/70 dark:text-blue-200/70">
                    {t("enduser.plan.crypto.amountToSend", { defaultValue: "Amount to Send" })}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {cryptoAmount !== undefined ? `${cryptoAmount}` : "-"} {data.crypto || ""}
                  </p>
                  <p className="text-xs text-blue-700/70 dark:text-blue-200/70">
                    {fiatAmount !== undefined ? `${fiatAmount}` : "-"} {fiatCurrency}
                  </p>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-200 sm:text-sm">
                  {data.message ||
                    t("enduser.plan.crypto.instructions", {
                      defaultValue: "Send payment to the address below to complete your transaction.",
                    })}
                </p>
              </div>

              <div className="mt-3">
                <p className="mb-1 text-[11px] uppercase tracking-wider text-blue-700/70 dark:text-blue-200/70">
                  {t("enduser.plan.crypto.walletAddress", { defaultValue: "Wallet Address" })}
                </p>
                <p className="break-all rounded-lg border border-blue-200/60 bg-white/80 p-3 font-mono text-sm text-gray-900 dark:border-blue-800/60 dark:bg-[#1f2326] dark:text-gray-100">
                  {walletAddress || "-"}
                </p>
                <button
                  type="button"
                  onClick={handleCopyAddress}
                  disabled={!walletAddress}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#1f2326] dark:text-gray-100 dark:hover:bg-[#262a2e]"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {copyState === "copied"
                    ? t("enduser.plan.crypto.addressCopied", { defaultValue: "Address copied" })
                    : copyState === "failed"
                      ? t("enduser.plan.crypto.copyFailed", { defaultValue: "Copy failed" })
                      : t("enduser.plan.crypto.copyAddress", { defaultValue: "Copy address" })}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#252628] sm:p-5">
              {qrCodeSource ? (
                <img
                  src={qrCodeSource}
                  alt={t("enduser.plan.crypto.qrCodeAlt", { defaultValue: "Crypto payment QR code" })}
                  className="h-48 w-48 rounded-xl border border-gray-200 bg-white object-contain p-2 dark:border-gray-700 dark:bg-[#1f2326]"
                />
              ) : (
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
                    <Loader2 className="h-7 w-7 animate-spin" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t("enduser.plan.crypto.generatingQr", { defaultValue: "Generating QR code" })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t("enduser.plan.crypto.scanFallback", {
                        defaultValue: "You can still copy the wallet address and pay manually.",
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.currencyLabel", { defaultValue: "Currency" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{fiatCurrency || "-"}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.payment.cryptoLabel", { defaultValue: "Crypto" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{data.crypto || "-"}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.amountInCrypto", { defaultValue: "Amount in Crypto" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {cryptoAmount !== undefined ? `${cryptoAmount}` : "-"} {data.crypto || ""}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.amountInFiat", { defaultValue: "Amount in Fiat" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {fiatAmount !== undefined ? `${fiatAmount}` : "-"} {fiatCurrency}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.platformFee", { defaultValue: "Platform Fee (Crypto)" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {data.platformFeeInCrypto !== undefined ? `${data.platformFeeInCrypto}` : "-"} {data.crypto || ""}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.payment.chainLabel", { defaultValue: "Network" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {data.network || data.chain || "-"}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.expiresAt", { defaultValue: "Expires At" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {data.expiresAt ? new Date(data.expiresAt).toLocaleString() : "-"}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.paymentStatus", { defaultValue: "Payment Status" })}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {isExpired
                  ? t("enduser.plan.crypto.expired", { defaultValue: "Expired" })
                  : t("enduser.plan.crypto.awaitingPayment", { defaultValue: "Awaiting payment" })}
              </p>
            </div>
          </div>

          {(data.reference || data.transactionId || data.txRef) && (
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t("enduser.plan.crypto.referenceLabel", { defaultValue: "Reference" })}
              </p>
              <p className="break-all text-sm font-semibold text-gray-900 dark:text-white">
                {data.reference || data.transactionId || data.txRef}
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex flex-col items-stretch justify-between gap-3 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#2a2b2e] sm:flex-row sm:items-center sm:p-6">
          <button
            type="button"
            onClick={onClose}
            className="order-2 rounded-lg px-6 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 sm:order-1"
          >
            {t("enduser.plan.actions.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={onPaid}
            disabled={isVerifying}
            className="order-1 flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-8 py-2.5 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:order-2"
          >
            {isVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isVerifying
              ? t("enduser.plan.crypto.verifying", { defaultValue: "Verifying..." })
              : t("enduser.plan.crypto.paid", { defaultValue: "I have paid" })}
          </button>
        </div>
      </div>
    </div>
  );
}
